import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  FileText, 
  ChevronRight, 
  Sparkles,
  X,
  Check
} from 'lucide-react';
import BRIEF_TEMPLATES from '@/config/briefTemplates';

/**
 * Gallery of pre-filled brief templates by business niche
 * Helps users start from a realistic example instead of a blank page
 */
const BriefTemplateGallery = ({ onSelectTemplate, onStartFromScratch, isVisible, onClose }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  if (!isVisible) return null;

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setShowPreview(true);
  };

  const handleConfirmTemplate = () => {
    if (selectedTemplate) {
      onSelectTemplate(selectedTemplate.brief);
      onClose();
    }
  };

  const handleStartFromScratch = () => {
    onStartFromScratch();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="font-display text-xl sm:text-2xl font-bold text-foreground">
              {showPreview ? 'Vista previa de plantilla' : 'Elige una plantilla para empezar'}
            </h2>
            <p className="text-muted-foreground mt-1">
              {showPreview 
                ? 'Revisa los datos de ejemplo. Podrás editarlos a tu caso.'
                : 'Empieza desde un ejemplo realista y adapta los datos a tu negocio'
              }
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors p-2"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!showPreview ? (
            <>
              {/* Template Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {BRIEF_TEMPLATES.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => handleSelectTemplate(template)}
                    className="group relative bg-background border border-border rounded-xl p-5 text-left hover:border-brand-300 dark:hover:border-brand-700 hover:shadow-lg transition-all duration-300"
                  >
                    {/* Icon */}
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${template.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <span className="text-2xl">{template.icon}</span>
                    </div>
                    
                    {/* Content */}
                    <h3 className="font-semibold text-foreground mb-1 group-hover:text-brand-600 transition-colors">
                      {template.name}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {template.description}
                    </p>
                    
                    {/* Arrow */}
                    <ChevronRight className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                  </button>
                ))}
              </div>

              {/* Start from scratch option */}
              <div className="border-t border-border pt-6">
                <button
                  onClick={handleStartFromScratch}
                  className="w-full flex items-center justify-center gap-3 py-4 px-6 rounded-xl border-2 border-dashed border-muted hover:border-brand-300 hover:bg-brand-50 dark:hover:bg-brand-900/10 transition-all group"
                >
                  <FileText className="w-5 h-5 text-muted-foreground group-hover:text-brand-500 transition-colors" />
                  <span className="text-muted-foreground group-hover:text-foreground transition-colors">
                    Empezar desde cero
                  </span>
                </button>
              </div>
            </>
          ) : (
            /* Template Preview */
            <div className="space-y-6">
              {/* Template header */}
              <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-xl">
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${selectedTemplate?.color} flex items-center justify-center`}>
                  <span className="text-3xl">{selectedTemplate?.icon}</span>
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-foreground">{selectedTemplate?.name}</h3>
                  <p className="text-sm text-muted-foreground">{selectedTemplate?.description}</p>
                </div>
              </div>

              {/* Preview fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <PreviewField 
                  label="Nombre del producto" 
                  value={selectedTemplate?.brief?.product_name} 
                />
                <PreviewField 
                  label="Precio" 
                  value={`${selectedTemplate?.brief?.price} ${selectedTemplate?.brief?.currency}`} 
                />
                <PreviewField 
                  label="Público objetivo" 
                  value={selectedTemplate?.brief?.target_audience} 
                  fullWidth
                />
                <PreviewField 
                  label="Promesa principal" 
                  value={selectedTemplate?.brief?.main_promise} 
                  fullWidth
                />
                <PreviewField 
                  label="Puntos de dolor" 
                  value={selectedTemplate?.brief?.pain_points} 
                  fullWidth
                />
                <PreviewField 
                  label="Objeciones principales" 
                  value={selectedTemplate?.brief?.main_objections} 
                  fullWidth
                />
              </div>

              {/* Info note */}
              <div className="flex items-start gap-3 p-4 bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-800 rounded-xl">
                <Sparkles className="w-5 h-5 text-brand-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-foreground">
                  <strong>Estos son datos de ejemplo.</strong> Una vez cargados, podrás editarlos completamente para adaptarlos a tu producto o servicio real.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border flex items-center justify-between gap-4">
          {showPreview ? (
            <>
              <Button
                variant="outline"
                onClick={() => setShowPreview(false)}
              >
                Volver a plantillas
              </Button>
              <Button
                onClick={handleConfirmTemplate}
                className="bg-gradient-to-r from-brand-500 to-violet-500 hover:from-brand-600 hover:to-violet-600 text-white"
              >
                <Check className="w-4 h-4 mr-2" />
                Usar esta plantilla
              </Button>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              Selecciona una plantilla para ver los datos de ejemplo
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const PreviewField = ({ label, value, fullWidth = false }) => (
  <div className={fullWidth ? 'md:col-span-2' : ''}>
    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
      {label}
    </label>
    <p className="mt-1 text-sm text-foreground bg-muted/30 rounded-lg p-3 border border-border">
      {value || '-'}
    </p>
  </div>
);

export default BriefTemplateGallery;
